import React from "react";
import Tooltip from "@mui/material/Tooltip";
import LookbackToggle from "./LookbackToggle";
import {
  convertToQuarterIndex,
  getLookbackRange,
  getMemberRoleTypes,
  isActiveEmployee,
  isDateWithinRange,
  registerRoleTypeName,
  safeLocaleCompare,
  getDayIndexFromDateString,
} from "./utils";

function getWeekStartForDateString(dateString, weekStartIndex = 1) {
  if (!dateString) {
    return null;
  }
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(dateString);
  if (!match) {
    return null;
  }
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if ([year, month, day].some((value) => Number.isNaN(value))) {
    return null;
  }
  const utcDate = new Date(Date.UTC(year, month - 1, day));
  if (Number.isNaN(utcDate.getTime())) {
    return null;
  }
  const dayIndex = utcDate.getUTCDay();
  const weekStart = new Date(utcDate);
  weekStart.setUTCDate(utcDate.getUTCDate() - ((dayIndex - weekStartIndex + 7) % 7));
  return weekStart.toISOString().slice(0, 10);
}

function calculateAvailabilityHours(entries, coverageByDow) {
  const availabilityByMember = new Map();
  entries?.forEach((entry) => {
    const memberId = entry?.store_team_member;
    if (!memberId) {
      return;
    }
    const dowIndex = Number(entry?.dow || 1) - 1;
    if (dowIndex < 0 || dowIndex > 6) {
      return;
    }
    const dowCoverage = coverageByDow?.get(dowIndex);
    if (!dowCoverage || dowCoverage.size === 0) {
      return;
    }
    const windows = [
      [entry?.available_from, entry?.available_to],
      [entry?.available_from_2, entry?.available_to_2],
    ];
    let total = 0;
    windows.forEach(([startRaw, endRaw]) => {
      const start = convertToQuarterIndex(startRaw);
      const end = convertToQuarterIndex(endRaw);
      if (start === null || end === null || end <= start) {
        return;
      }
      let countedQuarters = 0;
      for (
        let idx = Math.max(start, 0);
        idx < Math.min(end, 96);
        idx += 1
      ) {
        if (dowCoverage.has(idx)) {
          countedQuarters += 1;
        }
      }
      total += countedQuarters * 0.25;
    });
    if (total <= 0) {
      return;
    }
    availabilityByMember.set(
      memberId,
      (availabilityByMember.get(memberId) || 0) + total
    );
  });
  return availabilityByMember;
}

function calculateScheduledHours(shifts) {
  const scheduledByMember = new Map();
  shifts?.forEach((shift) => {
    if (shift?.other_store) {
      return;
    }
    const memberId = shift?.store_team_member;
    const hours = Number(shift?.hours ?? shift?.shift_length ?? 0);
    if (!memberId || Number.isNaN(hours) || hours <= 0) {
      return;
    }
    scheduledByMember.set(
      memberId,
      (scheduledByMember.get(memberId) || 0) + hours
    );
  });
  return scheduledByMember;
}

function resolveShiftDate(entry) {
  return (
    entry?.date ||
    entry?.date_start ||
    entry?.shift_date ||
    entry?.published?.date ||
    entry?.date_end ||
    entry?.published?.date_start ||
    null
  );
}

function calculateWorkedHours(executedShifts) {
  const workedByMember = new Map();
  executedShifts?.forEach((entry) => {
    const memberId =
      entry?.store_team_member?.id || entry?.published?.store_team_member?.id;
    if (!memberId) {
      return;
    }
    const start = Number(entry?.shift_start);
    const endRaw = Number(entry?.shift_end);
    const totalBreak = Number(entry?.total_break || 0);
    let hours = 0;
    if (!Number.isNaN(start) && !Number.isNaN(endRaw)) {
      let end = endRaw;
      if (end < start) {
        end += 24;
      }
      hours = Math.max(end - start - totalBreak, 0);
    } else if (entry?.published?.hours !== undefined) {
      hours = Number(entry.published.hours) || 0;
    }
    if (hours <= 0) {
      return;
    }
    workedByMember.set(memberId, (workedByMember.get(memberId) || 0) + hours);
  });
  return workedByMember;
}

function describeUtilizationColor(preferredHrs, scheduledHrs) {
  if (!preferredHrs || preferredHrs <= 0) {
    return "No preferred hours on file";
  }
  const variance = Math.abs((scheduledHrs || 0) - preferredHrs) / preferredHrs;
  if (variance <= 0.25) {
    return "On target (±25%)";
  }
  if (variance <= 0.4) {
    return "Slight variance (±40%)";
  }
  return "Needs attention (>40%)";
}

function renderUtilizationTooltip({
  availabilityHrs,
  scheduledHrs,
  preferredHrs,
  utilizationColor,
}) {
  const availabilityDisplay =
    availabilityHrs > 0 ? `${availabilityHrs.toFixed(1)} hrs` : "Not on file";
  const scheduledDisplay =
    scheduledHrs > 0 ? `${scheduledHrs.toFixed(1)} hrs` : "Not scheduled";
  const preferredDisplay =
    preferredHrs > 0 ? `${preferredHrs.toFixed(1)} hrs` : "No preferred hours";
  const detailItems = [
    {
      title: "Bar length",
      description: "Uses availability or a 40 hr fallback so low coverage stands out.",
    },
    {
      title: "Fill + marker",
      description: "Fill = scheduled hours, marker = employee’s preferred target.",
    },
  ];
  const statusLabel = describeUtilizationColor(preferredHrs, scheduledHrs);

  return (
    <div className="max-w-sm text-slate-900">
      <div className="rounded-2xl border border-slate-200 bg-white/95 shadow-lg">
        <div className="flex items-center justify-between gap-4 border-b border-slate-100 px-4 py-3">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
              Target hour bar
            </p>
            <p className="text-xs text-slate-500">Quick read on staffing vs employee goals.</p>
          </div>
          <div className="flex items-center gap-2 text-xs text-slate-600">
            <span
              className="inline-flex h-4 w-4 rounded"
              style={{ backgroundColor: utilizationColor || "#9ca3af" }}
            />
            <span className="font-semibold">{statusLabel}</span>
          </div>
        </div>
        <div className="divide-y divide-slate-100 text-xs text-slate-600">
          {detailItems.map((item) => (
            <div key={item.title} className="px-4 py-2">
              <p className="text-sm font-semibold text-slate-800">{item.title}</p>
              <p>{item.description}</p>
            </div>
          ))}
          <div className="grid gap-2 px-4 py-3 text-sm">
            {[{
              label: "Availability",
              value: availabilityDisplay,
            },
            {
              label: "Scheduled",
              value: scheduledDisplay,
            },
            {
              label: "Preferred",
              value: preferredDisplay,
            }].map((item) => (
              <div
                key={item.label}
                className="flex items-center justify-between rounded-xl bg-slate-50 px-3 py-2"
              >
                <span className="font-medium text-slate-900">{item.label}</span>
                <span>{item.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function getUtilizationColor(preferredHours, scheduledHours) {
  if (!preferredHours || preferredHours <= 0) {
    return "#94a3b8"; // slate-400
  }
  const variance = Math.abs(scheduledHours - preferredHours) / preferredHours;
  if (variance <= 0.25) {
    return "#22c55e"; // green-500
  }
  if (variance <= 0.4) {
    return "#f97316"; // orange-500
  }
  return "#ef4444"; // red-500
}

function buildForecastCoverageStats(futureForecast, weekStartIndex = 1) {
  const coverageByDow = new Map();

  if (!Array.isArray(futureForecast) || futureForecast.length === 0) {
    return { coverageByDow, averageOpenHoursPerWeek: null };
  }

  const quartersByWeek = new Map();

  futureForecast.forEach((entry) => {
    const hours = Number(entry?.hours || 0);
    const labor = Number(entry?.labor || 0);
    if (!(hours > 0 || labor > 0)) {
      return;
    }
    const quarterIndex = convertToQuarterIndex(entry?.qh_num);
    if (quarterIndex === null || quarterIndex < 0) {
      return;
    }
    const dateString = entry?.date;
    const weekKey = getWeekStartForDateString(dateString, weekStartIndex);
    const dowIndex = getDayIndexFromDateString(dateString);
    if (!weekKey || dowIndex === null || dowIndex < 0 || dowIndex > 6) {
      return;
    }

    let dowSet = coverageByDow.get(dowIndex);
    if (!dowSet) {
      dowSet = new Set();
      coverageByDow.set(dowIndex, dowSet);
    }
    dowSet.add(quarterIndex);

    let quarterSet = quartersByWeek.get(weekKey);
    if (!quarterSet) {
      quarterSet = new Set();
      quartersByWeek.set(weekKey, quarterSet);
    }
    quarterSet.add(`${dateString}_${quarterIndex}`);
  });

  if (quartersByWeek.size === 0) {
    return { coverageByDow, averageOpenHoursPerWeek: null };
  }

  const weeklyTotals = Array.from(quartersByWeek.values()).map(
    (quarterSet) => quarterSet.size / 4
  );

  if (weeklyTotals.length === 0) {
    return { coverageByDow, averageOpenHoursPerWeek: null };
  }

  const totalHours = weeklyTotals.reduce((sum, value) => sum + value, 0);
  return {
    coverageByDow,
    averageOpenHoursPerWeek: totalHours / weeklyTotals.length,
  };
}

function HeadcountPlanningRoster({
  planningData,
  lookbackWeeks,
  onLookbackChange,
  lookbackOptions,
}) {
  const historyRange = planningData?.historyRange;
  const lookbackRange = React.useMemo(
    () => getLookbackRange(historyRange, lookbackWeeks),
    [historyRange, lookbackWeeks]
  );

  const actualSchedule = planningData?.actualSchedule;
  const actualUsedSchedule = planningData?.actualUsedSchedule;

  const filteredActualSchedule = React.useMemo(() => {
    if (!actualSchedule) {
      return [];
    }
    return actualSchedule.filter((shift) =>
      isDateWithinRange(resolveShiftDate(shift), lookbackRange)
    );
  }, [actualSchedule, lookbackRange]);

  const filteredActualUsedSchedule = React.useMemo(() => {
    if (!actualUsedSchedule) {
      return [];
    }
    return actualUsedSchedule.filter((entry) =>
      isDateWithinRange(resolveShiftDate(entry), lookbackRange)
    );
  }, [actualUsedSchedule, lookbackRange]);

  const rosterRows = React.useMemo(() => {
    if (!planningData) {
      return [];
    }
    const roleTypeNameMap = planningData.roleTypeNameMap;
    const members = Array.from(planningData.membersById?.values() || []);
    const activeMembers = members.filter((member) => isActiveEmployee(member));
    const {
      coverageByDow,
      averageOpenHoursPerWeek,
    } = buildForecastCoverageStats(planningData.futureForecast, planningData.weekStartIndex);
    const availabilityByMember = calculateAvailabilityHours(
      planningData.defaultAvailability,
      coverageByDow
    );
    const scheduledByMember = calculateScheduledHours(filteredActualSchedule);
    const workedByMember = calculateWorkedHours(filteredActualUsedSchedule);
    const denominatorWeeks = Math.max(lookbackWeeks || 0, 1);

    return activeMembers
      .map((member) => {
        const firstName = member.first_name || "";
        const lastName = member.last_name || "";
        const rawSortIndex = Number(member?.sort_index);
        const sortIndex = Number.isFinite(rawSortIndex) ? rawSortIndex : null;
        const availabilityHours = availabilityByMember.get(member.id) || 0;
        const preferredHours = Number(member?.target_weekly_hours || 0);
        const scheduledHoursTotal = scheduledByMember.get(member.id) || 0;
        const workedHoursTotal = workedByMember.get(member.id) || 0;
        const avgScheduled = scheduledHoursTotal / denominatorWeeks;
        const avgWorked = workedHoursTotal / denominatorWeeks;
        const primaryRole =
          registerRoleTypeName(
            member?.primary_role_type_name,
            roleTypeNameMap
          ) || "—";
        const allRoles = getMemberRoleTypes(member, roleTypeNameMap);
        const utilizationColor = getUtilizationColor(
          preferredHours,
          avgScheduled
        );
        const displayMaxHours = Math.max(
          40,
          Math.min(availabilityHours,40),
          preferredHours,
          avgScheduled
        );
        const availabilityPercent =
          displayMaxHours > 0
            ? Math.min((availabilityHours / displayMaxHours) * 100, 100)
            : 0;
        const scheduledPercent =
          displayMaxHours > 0
            ? Math.min((avgScheduled / displayMaxHours) * 100, 100)
            : 0;
        const preferredPercent =
          preferredHours > 0 && displayMaxHours > 0
            ? Math.min((preferredHours / displayMaxHours) * 100, 100)
            : null;
        const availabilityOpenPercent =
          averageOpenHoursPerWeek && averageOpenHoursPerWeek > 0
            ? Math.min(
                (availabilityHours / averageOpenHoursPerWeek) * 100,
                100
              )
            : null;
        const showScheduledLabelInside = scheduledPercent >= 18;

        return {
          id: member.id,
          name: `${firstName} ${lastName}`.trim(),
          firstName,
          lastName,
          sortIndex,
          primaryRole,
          allRoles,
          availabilityHours,
          preferredHours,
          avgScheduled,
          avgWorked,
          availabilityPercent,
          availabilityOpenPercent,
          scheduledPercent,
          preferredPercent,
          utilizationColor,
          displayMaxHours,
          showScheduledLabelInside,
        };
      })
      .sort((a, b) => {
        const normalizedSortA = Number.isFinite(a.sortIndex)
          ? a.sortIndex
          : Number.MAX_SAFE_INTEGER;
        const normalizedSortB = Number.isFinite(b.sortIndex)
          ? b.sortIndex
          : Number.MAX_SAFE_INTEGER;
        if (normalizedSortA !== normalizedSortB) {
          return normalizedSortA - normalizedSortB;
        }

        const roleComparison = safeLocaleCompare(a.primaryRole, b.primaryRole);
        if (roleComparison !== 0) {
          return roleComparison;
        }

        const lastNameComparison = safeLocaleCompare(a.lastName, b.lastName);
        if (lastNameComparison !== 0) {
          return lastNameComparison;
        }

        return safeLocaleCompare(a.firstName, b.firstName);
      });
  }, [
    planningData,
    filteredActualSchedule,
    filteredActualUsedSchedule,
    lookbackWeeks,
  ]);

  return (
    <div className="space-y-6">
      <section className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
        <div className="flex flex-row items-center justify-between gap-3 mb-4 md:flex-col md:items-start">
          <div>
            <h3 className="text-lg font-semibold text-slate-900">
              Team utilization overview
            </h3>
            <p className="text-sm text-slate-600">
              Weekly averages over the last {lookbackWeeks} completed weeks.
            </p>
          </div>
          <LookbackToggle
            value={lookbackWeeks}
            options={lookbackOptions}
            onChange={onLookbackChange}
          />
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm table-fixed">
            {/* Make Utilization much wider */}
            <colgroup>
              <col className="w-[220px]" />          {/* Team member */}
              <col className="w-[90px]" />           {/* Availability */}
              <col className="w-[90px]" />           {/* Preferred */}
              <col className="w-[110px]" />          {/* Avg scheduled */}
              <col className="w-[110px]" />          {/* Avg worked */}
              <col className="w-[760px] lg:w-[680px] md:w-[560px]" /> {/* Utilization */}
            </colgroup>

            <thead>
              <tr className="text-left text-slate-500">
                <th className="py-2 pr-4 font-medium">Employee</th>
                <th className="py-2 pr-4 font-medium">
                  Availability
                </th>
                <th className="py-2 pr-4 font-medium">Target</th>
                <th className="py-2 pr-4 font-medium">Scheduled</th>
                <th className="py-2 pr-4 font-medium">Worked</th>
                <th className="py-2 font-medium">Utilization</th>
              </tr>
            </thead>

            <tbody className="divide-y divide-slate-200">
              {rosterRows.map((row) => (
                <tr key={row.id} className="bg-white">
                  <td className="py-3 pr-4">
                    <div className="font-medium text-slate-900">{row.name}</div>
                    <div className="text-xs text-slate-500">
                      {row.allRoles.length > 0 ? row.allRoles.join(", ") : "No roles assigned"}
                    </div>
                  </td>

                  {/* <td className="py-3 pr-4 text-slate-700 whitespace-nowrap">
                    {row.availabilityOpenPercent !== null ? (
                      <div className="flex flex-col leading-tight">
                        <span className="font-semibold text-slate-800">
                          {`${Math.round(row.availabilityOpenPercent)}%`}
                        </span>
                        <span className="text-xs text-slate-500">
                          {row.availabilityHours.toFixed(1)} hrs
                        </span>
                      </div>
                    ) : row.availabilityHours !== undefined &&
                      row.availabilityHours !== null ? (
                      `${row.availabilityHours.toFixed(1)} hrs`
                    ) : (
                      "—"
                    )}
                  </td> */}

                  <td className="py-3 pr-4 text-slate-700 whitespace-nowrap">
                    {row.availabilityOpenPercent ? `${Math.round(row.availabilityOpenPercent)}%` : "—"}
                  </td>
                  <td className="py-3 pr-4 text-slate-700 whitespace-nowrap">
                    {row.preferredHours ? row.preferredHours.toFixed(1) : "—"}
                  </td>
                  <td className="py-3 pr-4 text-slate-700 whitespace-nowrap">
                    {row.avgScheduled ? row.avgScheduled.toFixed(1) : "—"}
                  </td>
                  <td className="py-3 pr-4 text-slate-700 whitespace-nowrap">
                    {row.avgWorked ? row.avgWorked.toFixed(1) : "—"}
                  </td>

                  {/* Utilization (unchanged content, now gets lots more width) */}
        <td className="py-3 pr-4 md:pl-2 md:pr-1">
          {(() => {
            const availabilityHrs = Math.min(
              40,
              Math.max(0, Number(row.availabilityHours ?? 0))
            );
            const scheduledHrs = Math.max(0, Number(row.avgScheduled ?? 0));
            const preferredHrs = Math.max(0, Number(row.preferredHours ?? 0));
            const totalHrs = Math.max(availabilityHrs, 40);

            const pct = (hrs) =>
              `${Math.max(0, Math.min(100, (hrs / (totalHrs || 1)) * 100))}%`;

            return (
              <Tooltip
                placement="top"
                enterTouchDelay={0}
                slotProps={{
                  tooltip: {
                    sx: {
                      bgcolor: "transparent",
                      color: "inherit",
                      border: "none",
                      boxShadow: "none",
                      p: 0,
                      maxWidth: 360,
                    },
                  },
                  arrow: {
                    sx: {
                      color: "transparent",
                      "&:before": {
                        border: 0,
                        boxSizing: "border-box",
                      },
                    },
                  },
                }}
                title={
                  renderUtilizationTooltip({
                    availabilityHrs,
                    scheduledHrs,
                    preferredHrs,
                    utilizationColor: row.utilizationColor,
                  })
                }
              >
                <div className="flex items-center gap-3 w-full">
                  {/* room for below-the-bar preferred label */}
                  <div className="flex-1 relative h-4 pb-6">
                    {/* track */}
                    <div className="absolute inset-0 bg-white" />

                    {/* availability band (capacity) */}
                    <div
                      className="absolute inset-y-0 left-0 bg-slate-300"
                      style={{ width: pct(availabilityHrs) }}
                    />

                    {/* scheduled fill (status color) */}
                    {scheduledHrs > 0 && (
                      <div
                        className="absolute inset-y-0 left-0"
                        style={{
                          width: pct(scheduledHrs),
                          backgroundColor: row.utilizationColor || "#9ca3af", // gray fallback
                        }}
                      />
                    )}

                    {/* preferred tick + label */}
                    {preferredHrs > 0 && (
                      <>
                        <div
                          className="absolute top-[-2px] bottom-[-2px] w-px bg-slate-700"
                          style={{ left: pct(preferredHrs) }}
                        />
                        <div
                          className="absolute top-[22px] text-[11px] font-medium text-slate-700 px-0.5"
                          style={{
                            left: pct(preferredHrs),
                            transform: "translateX(-50%)",
                          }}
                        >
                          {preferredHrs.toFixed(0)}
                        </div>
                      </>
                    )}

                    {/* scheduled label (closer to bar) */}
                    {scheduledHrs > 0 && (
                      <div
                        className="absolute -top-5 text-[11px] font-semibold text-slate-700 px-0.5"
                        style={{ left: pct(scheduledHrs), transform: "translateX(-50%)" }}
                      >
                        {scheduledHrs.toFixed(0)}
                      </div>
                    )}
                  </div>

                  <div className="text-xs text-slate-600 w-14 text-right">
                    {preferredHrs > 0
                      ? `${((100 * scheduledHrs) / preferredHrs).toFixed(0)}%`
                      : ""}
                  </div>
                </div>
              </Tooltip>
            );
          })()}
        </td>




                </tr>
              ))}

              {rosterRows.length === 0 && (
                <tr>
                  <td colSpan={6} className="py-6 text-center text-slate-500">
                    No active team members found for this store.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

      </section>
    </div>
  );
}

export default HeadcountPlanningRoster;
